import type { SxProps, Theme } from '@mui/material';
import { Box, Collapse } from '@mui/material';
import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useIsWithinBreakpoints } from '../../hooks/useIsWithinBreakpoints';
import { useShallowMergeSxProps } from '../../hooks/useShallowMergeSxProps';
import { Icons } from '../../icons/Icons';
import { H, V } from '../../Layout';
import { useStyling } from '../../Theme';
import type { ActionType, SecuredActionType } from '../ActionType';
import ResponsiveActions from '../ResponsiveActions';
import {
    CollapseActionIconButton,
    DEFAULT_EDITABLE_PANEL_COLLAPSE_ACTION_TEST_ID_PREFIX
} from './CollapseActionIconButton';
import EditablePanelActions from './EditablePanelActions';
import { ExportChip } from './ExportChip';
import { HeaderCollapseButton } from './HeaderCollapseButton';

export const DEFAULT_EDITABLE_PANEL_TEST_ID = 'editablePanel';

export interface EditablePanelProps {
    /**
     * The header component to render.
     */
    Header: ReactNode;

    /**
     * The content of the panel.
     */
    children?: ReactNode;

    /**
     * Gap between children elements.
     *
     * @default 0
     */
    gap?: number;

    /**
     * Set to make the panel collapsable.
     *
     * @default false
     */
    isCollapsable?: boolean;

    /**
     * Style overrides for the root container.
     */
    sx?: object;

    /**
     * Style overrides for the header container.
     */
    headerSx?: SxProps<Theme>;

    /**
     * Style overrides for the header actions container.
     */
    headerActionsSx?: SxProps<Theme>;

    /**
     * Set to make the panel read-only.
     *
     * @default false
     */
    readonly?: boolean;

    /**
     * Set to make the panel comment-only.
     *
     * @default false
     */
    commentOnly?: boolean;

    securedActions?: SecuredActionType[];

    /**
     * Set to control the collapsed state of the panel.
     */
    collapsed?: boolean;

    /**
     * Test ID for the root container.
     *
     * @default 'editablePanel'
     */
    dataTestId?: string;

    /**
     * Set to make the panel collapsed by default.
     *
     * @default false
     */
    defaultCollapsed?: boolean;

    /**
     * Set to use responsive actions that show as a three-dot menu on mobile.
     *
     * @default false
     */
    useResponsiveActions?: boolean;

    /**
     * Called with the next collapsed value whenever the user toggles the panel.
     * Use this together with `collapsed` to lift and persist the collapsed state externally.
     */
    onCollapsedChange?: (collapsed: boolean) => void;

    /**
     * Set to allow collapsing the panel by clicking on the header.
     *
     * When this is enabled the `Header` MUST NOT contain any interactive elements that would conflict with
     * the collapse behavior and also result in invalid HTML element nesting.
     *
     * @default false
     */
    enableCollapseOnHeaderClick?: boolean;
}

export default function EditablePanel(props: EditablePanelProps) {
    const {
        gap = 0,
        children,
        Header,
        isCollapsable = false,
        sx = {},
        headerSx,
        headerActionsSx,
        readonly = false,
        commentOnly = false,
        securedActions = [],
        collapsed,
        dataTestId = DEFAULT_EDITABLE_PANEL_TEST_ID,
        defaultCollapsed = false,
        useResponsiveActions = false,
        onCollapsedChange,
        enableCollapseOnHeaderClick = false
    } = props;
    const { isMobile, isTablet } = useStyling();
    const mobileOrTablet = isMobile || isTablet;
    const ActionsLayout = mobileOrTablet ? H : Box;
    const panelContentId = useId();
    const [contentCollapsed, setContentCollapsed] = useState(defaultCollapsed);

    const toggleCollapsed = useCallback(() => {
        setContentCollapsed((s) => {
            const next = !s;
            onCollapsedChange?.(next);
            return next;
        });
    }, [onCollapsedChange]);

    const isSmallScreen = useIsWithinBreakpoints(['xs', 's', 'm']);
    const shouldUseResponsiveActions = useResponsiveActions && isSmallScreen;
    const wrapperStyle = useShallowMergeSxProps(
        {
            position: 'relative'
        },
        headerSx
    );

    const actionsStyle = useMemo<SxProps<Theme>>(() => {
        const sx = {
            zIndex: 1,
            width: 'fit-content'
        } as const;

        return sx;
    }, []);

    useEffect(() => {
        setContentCollapsed(collapsed ?? defaultCollapsed ?? false);
    }, [collapsed, defaultCollapsed]);

    const collapseAction = useMemo<ActionType>(
        () => ({
            icon: contentCollapsed ? Icons.UnfoldMore : Icons.UnfoldLess,
            onClick: toggleCollapsed,
            dataTestId: `${DEFAULT_EDITABLE_PANEL_COLLAPSE_ACTION_TEST_ID_PREFIX}-${dataTestId}`
        }),
        [contentCollapsed, dataTestId, toggleCollapsed]
    );

    const visibleSecuredActions = useMemo(() => securedActions.filter((action) => !action.hidden), [securedActions]);

    const responsiveSecuredActions = useMemo<SecuredActionType[]>(
        () => [...visibleSecuredActions, ...(isCollapsable ? [collapseAction] : [])],
        [visibleSecuredActions, isCollapsable, collapseAction]
    );

    const shouldUseInlineRightWrapper = readonly || commentOnly || !shouldUseResponsiveActions;
    const headerActionsContainerBaseSx = useMemo<SxProps<Theme>>(() => {
        const computedBaseSx: SxProps<Theme> = {
            justifyContent: 'flex-end',
            ...(shouldUseInlineRightWrapper ? {} : { width: { xs: 'fit-content', md: '100%' } })
        };
        return computedBaseSx;
    }, [shouldUseInlineRightWrapper]);
    const headerActionsContainerSx = useShallowMergeSxProps(headerActionsContainerBaseSx, headerActionsSx);

    return (
        <V sx={sx} data-testid={dataTestId}>
            <H sx={wrapperStyle}>
                {enableCollapseOnHeaderClick ? (
                    <HeaderCollapseButton
                        contentCollapsed={contentCollapsed}
                        panelContentId={panelContentId}
                        onClick={toggleCollapsed}
                    >
                        {Header}
                    </HeaderCollapseButton>
                ) : (
                    Header
                )}
                <H sx={headerActionsContainerSx}>
                    {readonly && (
                        <ExportChip titleToken="form.read.only.description" labelToken="form.read.only.label" />
                    )}
                    {commentOnly && (
                        <ExportChip titleToken="form.comment.only.description" labelToken="form.comment.only.label" />
                    )}
                    {shouldUseResponsiveActions ? (
                        <ResponsiveActions
                            isActivated
                            securedActions={responsiveSecuredActions}
                            forceCompactMode
                            showBorder
                            iconSx={{ my: 1 }}
                            containerSx={actionsStyle}
                        />
                    ) : (
                        <ActionsLayout sx={actionsStyle}>
                            <EditablePanelActions securedActions={visibleSecuredActions} />
                            {isCollapsable && (
                                <CollapseActionIconButton
                                    contentCollapsed={contentCollapsed}
                                    panelContentId={panelContentId}
                                    onClick={toggleCollapsed}
                                    dataTestId={dataTestId}
                                />
                            )}
                        </ActionsLayout>
                    )}
                </H>
            </H>
            <Collapse id={panelContentId} in={!contentCollapsed} sx={{ width: '100%' }}>
                <H sx={{ gap: gap, flexFlow: 'wrap', alignItems: 'flex-start' }}>{children}</H>
            </Collapse>
        </V>
    );
}
